import * as CartActions from "./cart-actions";
import { act, Actions, Effect } from "@ngrx/effects";
import { AppState } from "../state";
import { catchError, map, mergeMap, switchMap, take } from "rxjs/operators";
import { ErrorHandler, Injectable, Injector } from "@angular/core";
import { of } from "rxjs";
import { ofAction } from "ngrx-actions";
import { Store } from "@ngrx/store";

import {
  FirebaseWriteService,
  StorageService,
  ValidateCouponService,
  InstantSyncOrderService,
  CreateQuoteService,
  PinelabsUploadTxnService,
  PinelabsCancelTxnService,
  PinelabsCheckTxnService
} from "@tendercuts/providers";
import { GenericError, GenericServerResponse, SaleOrder } from "@tendercuts/models";
import { PlaceOrderService } from "../../providers/place-order-service";
import { PosQuote, ValidateCouponCode } from "../../models";
import { CartState } from ".";

@Injectable()
export class CartEffects {
  constructor(
    private actions$: Actions,
    private couponService: ValidateCouponService,
    private firebaseWriteService: FirebaseWriteService,
    private errorHandler: ErrorHandler,
    public store: Store<AppState>,
    private storage: StorageService,
    private placeOrderService: PlaceOrderService,
    private instantSyncOrderService: InstantSyncOrderService,
    private createQuoteService: CreateQuoteService,
    private pinelabsUploadTxnService: PinelabsUploadTxnService,
    private pinelabsCancelTxnService: PinelabsCancelTxnService,
    private pinelabsCheckTxnService: PinelabsCheckTxnService
  ) {}

  @Effect()
  applyDiscount$ = this.actions$.pipe(
    ofAction(CartActions.ActionQuoteApplyCoupon),
    switchMap((action: CartActions.ActionQuoteApplyCoupon) =>
      this.couponService
        .getData(
          this.couponService.getParams(
            action.payload.appState,
            action.payload.couponCode
          )
        )
        .pipe(
          switchMap((response: ValidateCouponCode[]) => {
            if (response[0] && response[0].status) {
              return [
                new CartActions.ActionQuoteCouponApplied(
                  response[0],
                  action.payload.couponCode
                ),
              ];
            }
            return [new CartActions.ActionInvalidCouponApplied()];
          }),
          catchError((err) =>
            of(new CartActions.ActionCartLoadFailed(new GenericError(err)))
          )
        )
    )
  );

  /**
   * Firebase fails due to internal Assertion error
   * If it fails our entire system got breaked.
   * To handle the firebase server we used try catch.
   */
  @Effect()
  createOrder$ = this.actions$.pipe(
    ofAction(CartActions.ActionCreateOrder),
    switchMap((action: CartActions.ActionCreateOrder) => {
      this.placeOrderService.getData(action.firebaseQuote).subscribe(
        (data) => {
          console.log(action.firebaseQuote.id + " -> " + "order Placed");
        },
        (err) => {
          this.errorHandler.handleError(err);
        }
      );
      try {
        this.firebaseWriteService.createOrder(action.firebaseQuote);
      } catch (error) {
        console.error(error);
      }
      return [
        new CartActions.ActionClearLocalCart(),
        new CartActions.ActionOrderPlaced(),
      ];
    })
  );

  /**
   * if the quote cartproducts have any comboproduct we used saleorder sync flow
   * To handle the api fails error we used try catch.
   */
  @Effect()
  createComboOrder$ = this.actions$.pipe(
    ofAction(CartActions.ActionCreateInstantSyncOrder),
    switchMap((action: CartActions.ActionCreateInstantSyncOrder) => {
      return this.instantSyncOrderService.getData(action.firebaseQuote).pipe(
        switchMap((data: SaleOrder[]) => {
          let saleOrder = data[0];
          return [
            new CartActions.ActionClearLocalCart(),
            new CartActions.ActionInstantSyncOrderSuccess(saleOrder),
          ];
        }),
        catchError((err) => {
          this.errorHandler.handleError(err);
          try {
            this.firebaseWriteService.createOrder(action.firebaseQuote);
          } catch (error) {
            console.error("firebase entry failed", error);
          }
          return of(new CartActions.ActionInstantSyncFailed());
        })
      );
    })
  );

  /**
   * when instant sync api fails
   * handled try catch to do delayed sync
   * and set error message is true and clear the local cart
   */
  @Effect()
  instantSyncFailed$ = this.actions$.pipe(
    ofAction(CartActions.ActionInstantSyncFailed),
    mergeMap((action: CartActions.ActionInstantSyncFailed) => {
      return [
        new CartActions.ActionClearLocalCart(),
        new CartActions.ActionCartLoadFailed(new GenericError("failed")),
      ];
    })
  );

  /**
   * while cancelling the bill cancelling the bill
   * this action will trigger and create the void quote
   * into the firebase and clear the local storage and cart
   */
  @Effect()
  createcartquote$ = this.actions$.pipe(
    ofAction(CartActions.ActionPostQuoteToFireBase),
    map((action: CartActions.ActionPostQuoteToFireBase) => {
      this.firebaseWriteService.voidQuote(action.firebaseQuote);
      return new CartActions.ActionClearCart();
    })
  );

  @Effect()
  addUser$ = this.actions$.pipe(
    ofAction(CartActions.ActionAddUser),
    mergeMap((action: CartActions.ActionAddUser) => {
      return [
        new CartActions.ActionRemoveDiscount(),
        new CartActions.ActionQuoteRemoveReward(),
        new CartActions.ActionReBuildCartItems(),
      ];
    })
  );

  /**
   * while clear the cart remove
   * the quote from local storage
   */
  @Effect()
  clearCart$ = this.actions$.pipe(
    ofAction(CartActions.ActionClearCart),
    map((action: CartActions.ActionClearCart) => {
      return new CartActions.ActionClearLocalCart();
    })
  );

  /**
   * remove the quote from local storage
   */
  @Effect()
  clearLocalCart$ = this.actions$.pipe(
    ofAction(CartActions.ActionClearLocalCart),
    map((action: CartActions.ActionClearLocalCart) => {
      this.storage.removeData("cart_quote");
      return new CartActions.ActionWriteSuccessful();
    })
  );

  /**
   * when product added to cart
   * quote computed and stored in redux & in local storage
   * if already subscription product in cart, dont add the elite product again
   */
  @Effect()
  addtoCartquote$ = this.actions$.pipe(
    ofAction(CartActions.ActionCartAddProduct),
    mergeMap(async (action: CartActions.ActionCartAddProduct) => {
      this.store.dispatch(new CartActions.ActionRemoveDiscount());

      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();

      if (action.product.isSubscription && cartState.quote.isElite) {
        return new CartActions.ActionUpdateQuoteSuccess(cartState.quote);
      }

      const updatedQuote: PosQuote = this.createQuoteService.addProductsToCart(
        action.product,
        action.weight,
        cartState.quote
      );

      return new CartActions.ActionUpdateQuoteSuccess(updatedQuote);
    })
  );

  /**
   * when product removed from cart
   * quote computed and stored in redux & in local storage
   */
  @Effect()
  removeproductCartquote$ = this.actions$.pipe(
    ofAction(CartActions.ActionCartRemoveProduct),
    mergeMap(async (action: CartActions.ActionCartRemoveProduct) => {
      this.store.dispatch(new CartActions.ActionRemoveDiscount());

      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();
      const updatedQuote: PosQuote =
        this.createQuoteService.removeProductsFromCart(
          action.payload,
          cartState.quote
        );

      return new CartActions.ActionUpdateQuoteSuccess(updatedQuote);
    })
  );

  /**
   * when product removed from cart
   * quote computed and stored in redux & in local storage
   */
  @Effect()
  reBuildCartItems$ = this.actions$.pipe(
    ofAction(CartActions.ActionReBuildCartItems),
    mergeMap(async (action: CartActions.ActionReBuildCartItems) => {
      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();
      this.createQuoteService.refreshQute(cartState.quote);

      return new CartActions.ActionUpdateQuoteSuccess(cartState.quote);
    })
  );

  /**
   * On applying reward points compute the quote totals and
   * quote stored in redux & in local storage
   */
  @Effect()
  applyRewardPoints$ = this.actions$.pipe(
    ofAction(CartActions.ActionQuoteApplyReward),
    mergeMap(async (action: CartActions.ActionQuoteApplyReward) => {
      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();
      const updatedQuote: PosQuote = this.createQuoteService.applyRewardPoints(
        action.rewardPoints,
        cartState.quote
      );
      return new CartActions.ActionUpdateQuoteSuccess(updatedQuote);
    })
  );

  /**
   * On removing reward points compute the quote totals and
   * quote stored in redux & in local storage
   */
  @Effect()
  removeRewardPoints$ = this.actions$.pipe(
    ofAction(CartActions.ActionQuoteRemoveReward),
    mergeMap(async (action: CartActions.ActionQuoteRemoveReward) => {
      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();
      const updatedQuote: PosQuote = this.createQuoteService.applyRewardPoints(
        0,
        cartState.quote
      );
      return new CartActions.ActionUpdateQuoteSuccess(updatedQuote);
    })
  );

  /**
   * On applying the coupon code, compute the quote totals and
   * quote stored in redux & in local storage
   */
  @Effect()
  quoteCouponApplied$ = this.actions$.pipe(
    ofAction(CartActions.ActionQuoteCouponApplied),
    mergeMap(async (action: CartActions.ActionQuoteCouponApplied) => {
      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();
      const updatedQuote: PosQuote = this.createQuoteService.applyCoupon(
        action.validateCouponCode,
        action.couponCode,
        cartState.quote
      );

      return new CartActions.ActionUpdateQuoteSuccess(updatedQuote);
    })
  );

  /**
   * On removing the coupon code, compute the quote totals and
   * quote stored in redux & in local storage
   */
  @Effect()
  quoteRemoveDiscount$ = this.actions$.pipe(
    ofAction(CartActions.ActionRemoveDiscount),
    mergeMap(async (action: CartActions.ActionRemoveDiscount) => {
      const cartState: CartState = await this.store
        .select((state) => state.cartState)
        .pipe(take(1))
        .toPromise();
      const updatedQuote: PosQuote = this.createQuoteService.removeCoupon(
        cartState.quote
      );

      return new CartActions.ActionUpdateQuoteSuccess(updatedQuote);
    })
  );

  /**
   * Update the local cart quote with current redux cart quote
   */
  @Effect()
  updateQuoteSuccess$ = this.actions$.pipe(
    ofAction(CartActions.ActionUpdateQuoteSuccess),
    map((action: CartActions.ActionUpdateQuoteSuccess) => {
      if (action.quote.cartProducts && action.quote.cartProducts.length > 0) {
        this.storage.storeData("cart_quote", action.quote);
      }
      return new CartActions.ActionWriteSuccessful();
    })
  );

  @Effect()
  uploadPinelabsBill$ = this.actions$.pipe(
    ofAction(CartActions.ActionUploadPinelabsBill),
    switchMap((action: CartActions.ActionUploadPinelabsBill) =>
      this.pinelabsUploadTxnService
        .getData(
          this.pinelabsUploadTxnService.getParams(
            "upload_billing",
            action.payload.transactionData,
          )
        )
        .pipe(
          switchMap((response: GenericServerResponse) => {

            if (response[0] && response[0].status) {
              action.quote.plutusTransactionReferenceID = response[0].result.PlutusTransactionReferenceID;
              this.storage.storeData("cart_quote", action.quote);
                return [
                  new CartActions.ActionUploadPinelabsBillDone(
                    response[0].result.PlutusTransactionReferenceID
                  ),
                ];
            }

          return [new CartActions.ActionUploadPinelabsBillFailed()];
          }),
          catchError((err) =>
            of(new CartActions.ActionCartLoadFailed(new GenericError(err)))
          )
        )
    )
  );

  @Effect()
  cancelPlutosTransaction$ = this.actions$.pipe(
    ofAction(CartActions.ActionCancelPlutosTransaction),
    switchMap((action: CartActions.ActionCancelPlutosTransaction) =>
      this.pinelabsCancelTxnService
        .getData(
          this.pinelabsCancelTxnService.getParams(
            "cancel_billing",
            action.payload.transactionData,
          )
        )
        .pipe(
          switchMap((response: GenericServerResponse) => {
            if (response[0] && response[0].status) {
              return [
                new CartActions.ActionPlutosTransactionCancelled(),
              ];
            }

            return [new CartActions.ActionCancelPlutosTransactionFailed()];
          }),
          catchError((err) =>
            of(new CartActions.ActionCartLoadFailed(new GenericError(err)))
          )
        )
    )
  );

  @Effect()
  checkPlutosTransactionStatus$ = this.actions$.pipe(
    ofAction(CartActions.ActionCheckPlutosTransactionStatus),
    switchMap((action: CartActions.ActionCheckPlutosTransactionStatus) =>
      this.pinelabsCheckTxnService
        .getData(
          this.pinelabsCheckTxnService.getParams(
            "check_billing",
            action.payload.transactionData,
          )
        )
        .pipe(
          switchMap((response: GenericServerResponse) => {
            if (response[0] && response[0].status) {
              if (response[0].result.ResponseCode === 0) {
                return [
                  new CartActions.ActionPlutosTransactionStatusChecked(true),
                ];
              }
              if (response[0].result.ResponseCode === 1001) {
                return [
                  new CartActions.ActionPlutosTransactionStatusChecked(false),
                ];
              }
            }

            return [
              new CartActions.ActionPlutosTransactionStatusFailed(false),
            ];
          }),
          catchError((err) =>
            of(new CartActions.ActionCartLoadFailed(new GenericError(err)))
          )
        )
    )
  );

}
